<script setup lang="ts">
import { formatCellphoneMasked, normalizeCellphoneDigits } from '@solfacil/frontend-utils/cellphone';
import { IconPlus } from '@tabler/icons-vue';
import { computed, ref } from 'vue';

import DataTable from '~/components/shared/DataTable.vue';
import DeleteWithReasonModal from '~/components/shared/DeleteWithReasonModal.vue';
import { useInactivatePartnerUser } from '~/store/coreUsers/useCoreUserMutations';
import usePartnerUsers, { type PartnerUser } from '~/store/coreUsers/usePartnerUsers';

import UserModal from './UserModal.vue';

const props = defineProps<{
  partnerId: number;
  partnerProfile?: string;
}>();

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const TABLE_COLUMNS = [
  {
    key: 'fullName',
    label: 'Nome',
    sort: true,
    width: '35%',
  },
  {
    key: 'email',
    label: 'E-mail',
    sort: true,
    width: '30%',
  },
  {
    key: 'mobile',
    label: 'Celular',
    sort: true,
    width: '25%',
  },
];

const currentPage = ref(DEFAULT_PAGE);
const pageSize = ref(DEFAULT_PAGE_SIZE);

const listParams = computed(() => ({
  page: currentPage.value,
  pageSize: pageSize.value,
  partnerId: props.partnerId,
}));

const {
  data: usersList,
  isLoading: isLoadingUsers,
  refetch: refetchUsers,
} = usePartnerUsers(listParams);

const inactivateMutation = useInactivatePartnerUser();

const isUserModalVisible = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');
const selectedUserId = ref<null | number>(null);

const isDeleteModalVisible = ref(false);
const selectedUserForDelete = ref<null | PartnerUser>(null);

const tableData = computed(() => usersList.value?.data ?? []);
const hasNext = computed(() => usersList.value?.hasNext ?? false);

async function handleConfirmDelete(reason: string) {
  if (!selectedUserForDelete.value) return;

  try {
    await inactivateMutation.mutateAsync({
      ativo: false,
      motivo: reason,
      partnerId: props.partnerId,
      userId: selectedUserForDelete.value.id,
    });
    isDeleteModalVisible.value = false;
    selectedUserForDelete.value = null;
  } catch {
    // handled by mutation
  }
}

function handleCreate() {
  modalMode.value = 'create';
  selectedUserId.value = null;
  isUserModalVisible.value = true;
}

function handleDeleteCancel() {
  selectedUserForDelete.value = null;
}

function handlePageChange(page: number) {
  if (hasNext.value || page < currentPage.value) {
    currentPage.value = page;
  }
}

function handlePageSizeChange(size: number) {
  currentPage.value = DEFAULT_PAGE;
  pageSize.value = size;
}

function openUserDelete(user: PartnerUser) {
  selectedUserForDelete.value = user;
  isDeleteModalVisible.value = true;
}

function openUserDetails(user: PartnerUser) {
  modalMode.value = 'view';
  selectedUserId.value = user.id;
  isUserModalVisible.value = true;
}

function openUserEdit(user: PartnerUser) {
  modalMode.value = 'edit';
  selectedUserId.value = user.id;
  isUserModalVisible.value = true;
}

defineExpose({ pageSize });
</script>

<template>
  <div class="col-12 mb-3">
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">Usuários</h3>
        <div class="card-options">
          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="handleCreate"
          >
            <IconPlus class="icon icon-sm me-1" :size="16" />
            Adicionar usuário
          </button>
        </div>
      </div>
      <div class="card-body py-3">
        <DataTable
          :columns="TABLE_COLUMNS"
          :data="tableData"
          :loading="isLoadingUsers"
          row-key="id"
          paginated
          :current-page="currentPage"
          :has-next="hasNext"
          :page-size="pageSize"
          empty-message="Nenhum usuário encontrado"
          @delete="openUserDelete"
          @details="openUserDetails"
          @edit="openUserEdit"
          @row-dblclick="openUserDetails"
          @page-change="handlePageChange"
          @page-size-change="handlePageSizeChange"
        >
          <template #cell-mobile="{ item }">
            <span class="text-secondary">
              {{ (item as PartnerUser).mobile
                ? formatCellphoneMasked(normalizeCellphoneDigits((item as PartnerUser).mobile))
                : '—' }}
            </span>
          </template>
        </DataTable>
      </div>
    </div>

    <UserModal
      v-model="isUserModalVisible"
      :mode="modalMode"
      :partner-id="partnerId"
      :partner-profile="partnerProfile"
      :user-id="selectedUserId"
      @success="refetchUsers"
    />

    <DeleteWithReasonModal
      v-model="isDeleteModalVisible"
      title="Inativar usuário"
      confirm-text="Inativar"
      :name="selectedUserForDelete?.fullName || ''"
      :email="selectedUserForDelete?.email || ''"
      :loading="inactivateMutation.isPending.value"
      @confirm="handleConfirmDelete"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>
